import './style.css';
import '../../tokens/index.css';
import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { recordBreadcrumb } from '@lumi/ui';
import router from './router';
import App from './App.vue';
import { applyStoredLocale, i18n, storedLocale } from './i18n';
import { configureActiveRoleModule, installChunkReloadRecovery, noteSessionHandoffFromUrl, installLocaleSync, registerAppKitI18n, registerDateFields, releaseBootFrame, syncHtmlLang } from '@lumi/app-kit';
import { initTheme } from '@lumi/ui';

// Role ids are module-scoped, so their storage key is too (#317 A62). This app
// persists the active role for the `ats` API; the tenant origin is shared with
// the other module UIs, and an unscoped key let whichever app the user last
// switched roles in decide what the others found.
configureActiveRoleModule('ats');

// Sync the colour theme (data-theme on <html>) with the reactive ref the
// header toggle binds to. The index.html pre-paint script already applied
// it; this reconciles + persists any ?theme= handoff.
initTheme();

// A cross-origin session handoff (`?token=…`) is a sign-in arriving on THIS origin,
// so the idle window starts now — not wherever the activity stamp this origin
// happens to hold was left by an earlier visit. Judged by that stale stamp, the
// first module pick after a login was signed out for inactivity mid-boot and
// bounced straight back to the workspace (issue 364 F11).
//
// BEFORE `createApp`, and never in a router guard: `IdleLogoutGuard`'s watcher is
// `immediate`, so it evaluates during the synchronous `app.mount()` — which runs
// before any `beforeEach` body gets its first microtask.
noteSessionHandoffFromUrl();

const app = createApp(App);
app.use(createPinia());
// A tab left open across a deploy no longer has the lazy chunks its build
// references; recover before the first navigation instead of dying silently.
installChunkReloadRecovery(router);
app.use(router);
app.use(i18n);
registerAppKitI18n(i18n.global);
// Every DateField: this locale, the month/year calendar and the typing hint (issue 823).
registerDateFields(i18n.global);
// Issue 508: keeps <html lang> in step with the active i18n locale so native
// date/datetime-local pickers (no shared component; see uiLocale.ts) pick up the
// same locale everywhere the browser honours it.
syncHtmlLang(i18n.global.locale);
// Issue 673: a language switched in another app of this workspace (or another
// tab) reaches this page when it is returned to, not only on its next reload.
installLocaleSync({ read: storedLocale, current: () => i18n.global.locale.value, apply: applyStoredLocale });

app.config.errorHandler = (err, instance, info) => {
  const component = (instance as any)?.type?.__file || (instance as any)?.type?.name || 'unknown';
  recordBreadcrumb({ type: 'error', message: (err as any)?.message ?? String(err) });
  console.error('[GlobalError]', info, component, err);
};

window.addEventListener('unhandledrejection', (event) => {
  recordBreadcrumb({ type: 'error', message: (event.reason as any)?.message ?? String(event.reason) });
  console.error('[UnhandledRejection]', event.reason);
});

app.mount('#app');
// index.html's boot frame stands in for the shell until the first route has
// resolved (issue 533); this lifts it then.
releaseBootFrame(router);
